import { isDefined } from "@clipboard-health/util-ts";
import { put } from "@src/appV2/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type BreakPolicy, type TimeRange } from "@src/appV2/Shifts/Shift/types";
import {
  getExtraWorkedTime,
  getMissedPunchExtraWorkedTime,
  type MissedPunch,
} from "@src/appV2/Shifts/Shift/WorkedBreakAuthorization/utils";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { DEFAULT_TIMESHEET_LOCATION } from "./useSubmitShiftTimeSheet";

export interface CaliforniaTimecardFile {
  url: string;
  filename: string;
  fileStorageFileKey: string;
}

export interface SubmitCaliforniaShiftTimeSheetParameters {
  clockInOut: TimeRange;
  lunchInOut?: TimeRange;
  shiftId: string;
  facilityEmployeeName?: string;
  hcpName: string;
  stationWingUnitFloor?: string;
  nursingServiceAssignment?: string;
  breakAuthorizationReason?: string;
  missedPunches?: MissedPunch[];
  actualClockInOut?: TimeRange;
  fullShiftExtraWorkedTimeReason?: string;
  actualLunchInOut?: TimeRange;
  location?: {
    latitude: string;
    longitude: string;
  };
  hcpTimecard: CaliforniaTimecardFile;
  facilityTimecard?: CaliforniaTimecardFile;
  breakPolicy?: BreakPolicy;
  forceAttachBreakAuthorizationReason?: boolean;
}

const SIGNED_TYPE = {
  HCP: "HCP",
  HCF: "HCF",
} as const;

const TIMESHEET_TYPE = {
  PHOTO: "photo",
} as const;

export function useSubmitCaliforniaShiftTimeSheet() {
  return useMutation({
    mutationFn: async (data: SubmitCaliforniaShiftTimeSheetParameters) => {
      const {
        clockInOut,
        lunchInOut,
        shiftId,
        facilityEmployeeName,
        hcpName,
        stationWingUnitFloor,
        nursingServiceAssignment,
        breakAuthorizationReason,
        missedPunches,
        actualClockInOut,
        fullShiftExtraWorkedTimeReason,
        location = DEFAULT_TIMESHEET_LOCATION,
        hcpTimecard,
        facilityTimecard,
        breakPolicy,
        forceAttachBreakAuthorizationReason,
      } = data;

      // Get break extraWorkedTime
      const breakExtraWorkedTime = getExtraWorkedTime({
        breakPolicy,
        forceAttachBreakAuthorizationReason,
        clockInOut,
        lunchInOut,
        breakAuthorizationReason,
      });

      const missedPunchExtraWorkedTime = getMissedPunchExtraWorkedTime({
        missedPunches,
      });

      const fullShiftExtraWorkedTime =
        isDefined(fullShiftExtraWorkedTimeReason) && isDefined(actualClockInOut)
          ? [
              {
                type: "FULL_SHIFT" as const,
                authorization: { reason: fullShiftExtraWorkedTimeReason },
              },
            ]
          : [];

      const allExtraWorkedTime = [
        ...(breakExtraWorkedTime ?? []),
        ...missedPunchExtraWorkedTime,
        ...fullShiftExtraWorkedTime,
      ];
      const extraWorkedTime = allExtraWorkedTime.length > 0 ? allExtraWorkedTime : undefined;

      const timecard = [
        {
          ...hcpTimecard,
          timesheet: TIMESHEET_TYPE.PHOTO,
          signedType: SIGNED_TYPE.HCP,
          digitallySignedBy: hcpName,
        },
        ...(isDefined(facilityTimecard)
          ? [
              {
                ...facilityTimecard,
                timesheet: TIMESHEET_TYPE.PHOTO,
                signedType: SIGNED_TYPE.HCF,
                digitallySignedBy: facilityEmployeeName ?? "",
              },
            ]
          : []),
      ];

      await put({
        url: `/v2/shifts/timecard/${shiftId}`,
        data: {
          timecard,
          submitClockInOut: clockInOut,
          submitLunchInOut: lunchInOut,
          digitallySignedByHCP: hcpName,
          extraWorkedTime,
          location: [location.longitude, location.latitude],
          ...(facilityEmployeeName && { digitallySignedBy: facilityEmployeeName }),
          ...(stationWingUnitFloor && { stationWingUnitFloor }),
          ...(nursingServiceAssignment && { nursingServiceAssignment }),
        },
        responseSchema: z.unknown(),
      });

      return { hcpTimecard, facilityTimecard };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.ERROR_SUBMITTING_SHIFT_TIME_SHEET,
    },
  });
}
